if (!customElements.get('kik-scrollable-table')) {
    class KikScrollableTable extends HTMLElement {
        constructor() {
            super();
            this._onScroll = this._onScroll.bind(this);
            this._onResize = this._onResize.bind(this);
        }

        // Getters
        get scrollContainer() {
            return this.querySelector('.kik-scrollable-table__table');
        }

        get prevBtn() {
            return this.querySelector('.kik-scrollable-table__nav-btn--prev');
        }

        get nextBtn() {
            return this.querySelector('.kik-scrollable-table__nav-btn--next');
        }

        get scrollbarThumb() {
            return this.querySelector('.kik-scrollable-table__scrollbar-thumb');
        }

        get scrollbarTrack() {
            return this.querySelector('.kik-scrollable-table__scrollbar-track');
        }

        get closeBtn() {
            return this.querySelector('[id^="kikModalClose-"]');
        }

        // Lifecycle: element added to DOM
        connectedCallback() {
            this._bindEvents();
            // Initial state after a frame so layout is ready
            requestAnimationFrame(() => {
                this._updateScrollbar();
                this._updateNavButtons();
            });
        }

        // Lifecycle: element removed from DOM
        disconnectedCallback() {
            this._unbindEvents();
        }

        // ── Event binding ──
        _bindEvents() {
            const container = this.scrollContainer;
            if (container) {
                container.addEventListener('scroll', this._onScroll, { passive: true });
            }

            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', () => this.scrollPrev());
            }

            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', () => this.scrollNext());
            }

            if (this.closeBtn) {
                this.closeBtn.addEventListener('click', () => this._hide());
            }

            this.addEventListener('keyup', (event) => {
                if (event.code.toUpperCase() === 'ESCAPE') this._hide();
            });

            this.addEventListener('click', (event) => {
                if (event.target === this) this._hide();
            });

            this._resizeObserver = new ResizeObserver(this._onResize);
            if (container) {
                this._resizeObserver.observe(container);
            }
        }

        _unbindEvents() {
            const container = this.scrollContainer;
            if (container) {
                container.removeEventListener('scroll', this._onScroll);
            }
            if (this._resizeObserver) {
                this._resizeObserver.disconnect();
            }
        }

        // ── Hide (delegates to parent modal) ──
        _hide() {
            const modal = this.closest('kik-modal-dialog');
            if (modal && typeof modal.hide === 'function') {
                modal.hide();
            }
        }

        // ── Scroll handlers ──
        _onScroll() {
            this._updateScrollbar();
            this._updateNavButtons();
        }

        _onResize() {
            this._updateScrollbar();
            this._updateNavButtons();
        }

        // ── Scroll by one column width ──
        _getScrollStep() {
            const th = this.scrollContainer.querySelector('thead th:not(.sticky-cell):not(.thead-empty-td)');
            if (th) return th.offsetWidth;
            return 245;
        }

        scrollPrev() {
            const container = this.scrollContainer;
            if (!container) return;
            container.scrollBy({ left: -this._getScrollStep(), behavior: 'smooth' });
        }

        scrollNext() {
            const container = this.scrollContainer;
            if (!container) return;
            container.scrollBy({ left: this._getScrollStep(), behavior: 'smooth' });
        }

        // ── Update nav button disabled state ──
        _updateNavButtons() {
            const container = this.scrollContainer;
            if (!container || !this.prevBtn || !this.nextBtn) return;

            const { scrollLeft, scrollWidth, clientWidth } = container;
            const tolerance = 2;

            this.prevBtn.disabled = scrollLeft <= tolerance;
            this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - tolerance;
        }

        // ── Update custom scrollbar thumb ──
        _updateScrollbar() {
            const container = this.scrollContainer;
            const thumb = this.scrollbarThumb;
            const track = this.scrollbarTrack;
            if (!container || !thumb || !track) return;

            const { scrollLeft, scrollWidth, clientWidth } = container;
            const trackWidth = track.offsetWidth;

            if (scrollWidth <= clientWidth) {
                thumb.style.display = 'none';
                return;
            }

            thumb.style.display = '';

            const scrollRatio = scrollLeft / (scrollWidth - clientWidth);
            const thumbWidth = Math.max(scrollRatio * trackWidth, 2);

            thumb.style.width = thumbWidth + 'px';
        }
    }

    customElements.define('kik-scrollable-table', KikScrollableTable);
}
